import React, { useState, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { productionRoutes, findProductionRoute } from './productionRoutes';
import SidebarNav from './SidebarNav';
import TopBar from './TopBar';

const RAIL_WIDTH_EXPANDED = 240;
const RAIL_WIDTH_COLLAPSED = 64;
const STORAGE_KEY = 'prod_sidebar_collapsed';

/**
 * ProductionShell — Phase 9 replacement for the Phase 8 `ProductionLayout`.
 *
 * Renders a permanent collapsible left sidebar on `md+` screens (primary
 * target: desktop + iPad landscape) and a temporary drawer behind a
 * hamburger on smaller viewports. The page title in the TopBar is sourced
 * from `productionRoutes` so each view container stays unaware of routing.
 *
 * Scope: wraps the authenticated `/production/*` routes.
 */
export default function ProductionShell() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() =>
    typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) === '1'
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* storage unavailable (private mode, quota) — ignore */
      }
      return next;
    });
  }, []);

  // Mobile drawer always renders expanded content; only desktop collapses.
  const effectiveCollapsed = isDesktop ? collapsed : false;
  const drawerWidth = effectiveCollapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH_EXPANDED;

  const currentTitle = useMemo(
    () => findProductionRoute(location.pathname)?.title ?? 'Production',
    [location.pathname],
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }} data-testid="production-shell">
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop || mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: 'width 200ms cubic-bezier(0.4,0.0,0.2,1)',
          },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: effectiveCollapsed ? 'center' : 'space-between',
            px: 2,
          }}
        >
          {!effectiveCollapsed && (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Production
            </Typography>
          )}
          {isDesktop && (
            <Tooltip title={effectiveCollapsed ? 'Expand' : 'Collapse'}>
              <IconButton
                onClick={toggleCollapsed}
                size="small"
                data-testid="sidebar-collapse-btn"
                aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {effectiveCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
        <SidebarNav routes={productionRoutes} collapsed={effectiveCollapsed} />
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* On desktop the permanent sidebar already carries the title + nav, so
            the TopBar is dropped to reclaim vertical space. Mobile still needs it
            for the hamburger that opens the temporary nav drawer. */}
        {!isDesktop && (
          <TopBar
            title={currentTitle}
            onOpenMobileNav={() => setMobileOpen(true)}
            isDesktop={isDesktop}
          />
        )}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
